/**
 * analyze() 主编排 —— 逐行对拍 climb-core/src/lib.rs:499-969
 *
 * 输入全分辨率 RGBA；内部分析分辨率 = analysisSize（≤maxDim 不放大）。
 * YOLO+EdgeSAM 会话齐备走主路；否则整图墙面差分回退。
 */
import type { InferenceSession } from "onnxruntime-web";
import { computeHsv, rgbToLab, hueDist } from "./color";
import { labelComponents, gatherStats, voronoiReconstruct } from "./conn";
import { open as morphOpen, dilate as morphDilate } from "./morph";
import { buildWallField, wallRef, localWallRef, classifyStats } from "./wall";
import { holdFromPixels, holdFromEllipse, wallDistHoldsInCrop } from "./holds";
import { buildClusters } from "./cluster";
import { resizeTriangle } from "./resize";
import { sampleLogit, samWindow, samMargin, samDecodeWithExpansion } from "./samgrid";
import { detectHolds, detectPersons } from "../infer/yolo";
import { encode, maskForBox } from "../infer/edgesam";
import {
  HoldKind,
  defaultParams,
  type Analysis,
  type Hold,
  type Params,
} from "./types";

export interface InferSessions {
  yolo?: InferenceSession;
  samEncoder?: InferenceSession;
  samDecoder?: InferenceSession;
  /** 人物检测（PicoDet，ADR 0002）；缺省时人物保色路径跳过 */
  person?: InferenceSession;
}

function labImage(rgba: Uint8ClampedArray, n: number): Float32Array {
  const lab = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const [l, a, b] = rgbToLab(rgba[i * 4], rgba[i * 4 + 1], rgba[i * 4 + 2]);
    lab[i * 3] = l;
    lab[i * 3 + 1] = a;
    lab[i * 3 + 2] = b;
  }
  return lab;
}

/** 检测框去重（质心距离 或 bbox IoU>0.5）。对拍 lib.rs:740-763 */
function isDup(holds: Hold[], hf: Hold, centroidFactor: number): boolean {
  const r = Math.sqrt(hf.area / Math.PI);
  for (const e of holds) {
    const re = Math.sqrt(e.area / Math.PI);
    const dx = e.centroid[0] - hf.centroid[0];
    const dy = e.centroid[1] - hf.centroid[1];
    const centroidDup = Math.sqrt(dx * dx + dy * dy) < (r + re) * centroidFactor;
    const ix0 = Math.max(e.bbox[0], hf.bbox[0]);
    const iy0 = Math.max(e.bbox[1], hf.bbox[1]);
    const ix1 = Math.min(e.bbox[2], hf.bbox[2]);
    const iy1 = Math.min(e.bbox[3], hf.bbox[3]);
    const inter = Math.max(0, ix1 - ix0) * Math.max(0, iy1 - iy0);
    const ea = (e.bbox[2] - e.bbox[0]) * (e.bbox[3] - e.bbox[1]);
    const fa = (hf.bbox[2] - hf.bbox[0]) * (hf.bbox[3] - hf.bbox[1]);
    const iou = ea + fa - inter > 1 ? inter / (ea + fa - inter) : 0;
    if (centroidDup || iou > 0.5) return true;
  }
  return false;
}

export async function analyze(
  full: Uint8ClampedArray,
  fw: number,
  fh: number,
  sessions: InferSessions,
  p: Params = defaultParams(),
  cb: (stage: string, pct?: number) => void = () => {},
): Promise<Analysis> {
  const t0 = performance.now();
  cb("预处理图像");
  // 分析分辨率（lib.rs:507-516；不放大，最小 32，Triangle 重采样）
  const scale = Math.min(p.analysisMaxDim / Math.max(fw, fh), 1);
  const aw = Math.min(Math.max(Math.round(fw * scale), 32), fw);
  const ah = Math.min(Math.max(Math.round(fh * scale), 32), fh);
  const img =
    aw === fw && ah === fh
      ? Uint8ClampedArray.from(full)
      : Uint8ClampedArray.from(resizeTriangle(full, fw, fh, aw, ah, 4));
  const n = aw * ah;

  cb("计算颜色空间");
  const { hue, sat, val } = computeHsv(img);

  const minPx = Math.trunc(p.minAreaFrac * n);
  let holds: Hold[] = [];
  let labels: Uint32Array = new Uint32Array(0);
  let person: Uint32Array | null = null;

  if (sessions.yolo) {
    cb("YOLO 检测岩点");
    const boxes = await detectHolds(sessions.yolo, img, aw, ah);

    let emb: Awaited<ReturnType<typeof encode>> = null;
    if (sessions.samEncoder && sessions.samDecoder) {
      cb("EdgeSAM 图像编码");
      emb = await encode(sessions.samEncoder, img, aw, ah);
    }
    // SAM 路径需要全图 Lab（墙证据判定）
    const lab = emb ? labImage(img, n) : null;

    cb("SAM 掩码解码");
    // 逐框扩框闭环（对拍 lib.rs:551-583）
    const grids: Array<{ grid: Float32Array; finalBox: [number, number, number, number] } | null> = [];
    if (emb && sessions.samDecoder) {
      const dec = sessions.samDecoder;
      let bi = 0;
      for (const d of boxes) {
        // #15：逐框进度（每 10 框一条，防消息风暴）——手机上解码慢，
        // 用户能看到 n/N 在动而非「一直卡在 SAM 掩码解码」；
        // 2026-10-22：附真百分比驱动扫描层确定性进度条
        if (bi % 10 === 0)
          cb(
            `SAM 掩码解码 ${bi + 1}/${boxes.length}`,
            boxes.length > 0 ? (bi + 1) / boxes.length : undefined,
          );
        const r = await samDecodeWithExpansion(d.box, d.cls, emb.scale, aw, ah, async (b) => {
          const m = await maskForBox(dec, emb, b);
          return m ? m.grid : null;
        });
        grids.push(r ? { grid: r.grid, finalBox: r.finalBox } : null);
        bi++;
      }
    } else {
      for (let i = 0; i < boxes.length; i++) grids.push(null);
    }

    for (let bi = 0; bi < boxes.length; bi++) {
      const d = boxes[bi];
      // 最终提示框（可能已被扩框放大）；无 SAM 时用 YOLO 原框
      const b = grids[bi]?.finalBox ?? d.box;
      const bw = b[2] - b[0];
      const bh = b[3] - b[1];
      if (bw < 5 || bh < 5) continue;
      const ex = samMargin(d.cls);
      const x0 = Math.trunc(Math.max(b[0] - bw * ex, 0));
      const y0 = Math.trunc(Math.max(b[1] - bh * ex, 0));
      const x1 = Math.ceil(Math.min(b[2] + bw * ex, aw));
      const y1 = Math.ceil(Math.min(b[3] + bh * ex, ah));
      const cw = Math.max(x1 - x0, 0);
      const chh = Math.max(y1 - y0, 0);
      if (cw < 5 || chh < 5) continue;

      // 1) EdgeSAM 框提示掩码 ∩ 非墙证据，连通域拆分
      const found: Hold[] = [];
      let samOk = false;
      const g = grids[bi];
      if (emb && lab && g) {
        const wall = localWallRef(lab, sat, aw, x0, y0, cw, chh);
        const tLo = wall ? Math.min(Math.max(p.wallK * wall.sigma * 0.55, 4.0), 18.0) : undefined;
        const samPx: number[] = [];
        const samBin = new Uint8Array(cw * chh);
        const maskBin = new Uint8Array(cw * chh);
        for (let y = y0; y < y1; y++) {
          for (let x = x0; x < x1; x++) {
            if (sampleLogit(emb.scale, g.grid, x + 0.5, y + 0.5) > 0) {
              const gi = y * aw + x;
              samPx.push(gi);
              samBin[(y - y0) * cw + (x - x0)] = 255;
              const keep =
                wall && tLo !== undefined
                  ? Math.sqrt(
                      (lab[gi * 3] - wall.med[0]) ** 2 +
                        (lab[gi * 3 + 1] - wall.med[1]) ** 2 +
                        (lab[gi * 3 + 2] - wall.med[2]) ** 2,
                    ) > tLo
                  : true;
              if (keep) maskBin[(y - y0) * cw + (x - x0)] = 255;
            }
          }
        }
        // 连通域拆分（窗口局部索引）
        const { labels: lbl2, count: cnt2 } = labelComponents(maskBin, cw, chh);
        const comps: number[][] = Array.from({ length: cnt2 }, () => []);
        for (let i = 0; i < lbl2.length; i++) {
          const l = lbl2[i];
          if (l > 0) comps[l - 1].push(i);
        }
        comps.sort((a2, b2) => b2.length - a2.length);
        comps.length = Math.min(comps.length, 4);
        const compsF = comps.filter((c) => c.length >= 24);
        // 测地重构（issue #8）：证据∩SAM 种子在 SAM 掩码内扩张，面积上限防过冲
        const seeds = compsF.map((c) => Uint32Array.from(c));
        const caps = compsF.map((c) => Math.max(c.length * 4, c.length + 2000));
        const grown = voronoiReconstruct(samBin, cw, chh, seeds, caps);
        for (const gpx of grown) {
          if (gpx.length < 24) continue;
          const cpx = new Uint32Array(gpx.length);
          for (let k2 = 0; k2 < gpx.length; k2++) {
            const i = gpx[k2];
            const lx = i % cw;
            const ly = Math.floor(i / cw);
            cpx[k2] = (y0 + ly) * aw + (x0 + lx);
          }
          const hf = holdFromPixels(img, aw, cpx, p);
          if (hf) {
            found.push(hf);
            samOk = true;
          }
        }
        // 交集为空（岩点颜色≈墙面）：退回完整 SAM 掩码
        if (!samOk && samPx.length >= 24) {
          const hf = holdFromPixels(img, aw, Uint32Array.from(samPx), p);
          if (hf) {
            found.push(hf);
            samOk = true;
          }
        }
      }

      // 2) 回退：框内墙面差分（blob 覆盖 <40% 框面积时与椭圆并集）
      if (!samOk) {
        let fb = wallDistHoldsInCrop(
          img,
          aw,
          x0,
          y0,
          cw,
          chh,
          undefined,
          p,
          Math.trunc(Math.max(minPx * 0.25, 12)),
          cw * chh,
        );
        if (fb.length === 0) {
          const hf = holdFromEllipse(img, aw, x0, y0, cw, chh, p);
          if (hf) fb.push(hf);
        } else {
          // 只保留中心在检测框内（±10% 放宽）的最大 3 个 blob
          const cxr: [number, number] = [b[0] - bw * 0.1, b[2] + bw * 0.1];
          const cyr: [number, number] = [b[1] - bh * 0.1, b[3] + bh * 0.1];
          fb = fb.filter(
            (hf) =>
              hf.centroid[0] >= cxr[0] &&
              hf.centroid[0] <= cxr[1] &&
              hf.centroid[1] >= cyr[0] &&
              hf.centroid[1] <= cyr[1],
          );
          fb.sort((a2, b2) => b2.area - a2.area);
          fb.length = Math.min(fb.length, 3);
          let blobArea = 0;
          for (const hf of fb) blobArea += hf.area;
          if (fb.length > 0 && blobArea * 5 < cw * chh * 2) {
            const ell = holdFromEllipse(img, aw, x0, y0, cw, chh, p);
            if (ell) {
              const biggest = fb[0];
              const set = new Set<number>(biggest.ownPixels);
              for (const i of ell.ownPixels) set.add(i);
              biggest.ownPixels = Uint32Array.from(set);
              biggest.area = biggest.ownPixels.length;
              const bxx: [number, number, number, number] = [0xffffffff, 0xffffffff, 0, 0];
              let sx = 0;
              let sy = 0;
              for (const i of biggest.ownPixels) {
                const x = i % aw;
                const y = Math.floor(i / aw);
                sx += x;
                sy += y;
                if (x < bxx[0]) bxx[0] = x;
                if (y < bxx[1]) bxx[1] = y;
                if (x + 1 > bxx[2]) bxx[2] = x + 1;
                if (y + 1 > bxx[3]) bxx[3] = y + 1;
              }
              biggest.bbox = bxx;
              biggest.centroid = [sx / biggest.area, sy / biggest.area];
            }
          }
        }
        found.push(...fb);
      }

      for (const hf of found) {
        if (isDup(holds, hf, 0.6)) continue;
        hf.id = holds.length;
        holds.push(hf);
      }
    }
    // ---- 人物检测（可选模型；框 → 复用 EdgeSAM embedding 出轮廓，扣除岩点并集。
    //      对拍 lib.rs:765-799）。#15 第三轮：可选模型任何失败（IO 名不符/模型坏/
    //      推理异常）只降级为「无人物保色」，绝不允许炸掉整条线路识别 ----
    if (sessions.person && emb && sessions.samDecoder) {
      try {
        const pboxes = await detectPersons(sessions.person, img, aw, ah);
      if (pboxes.length > 0) {
        cb("人物轮廓分割");
        const pbin = new Uint8Array(n);
        // 对拍 lib.rs:771 `pboxes.iter().take(3)`：人物框按置信度序至多解码 3 个
        // （#15：补齐 web 侧缺失的 top3 截断——人群场景 yolo11n 框数无上限会放大解码负担，
        //   且两端同图输出需可对拍；detectPersons 输出即 NMS 置信度降序）
        for (const pb of pboxes.slice(0, 3)) {
          const win = samWindow(pb.box, 0.1, aw, ah);
          const m = await maskForBox(sessions.samDecoder, emb, pb.box);
          if (!m) continue;
          for (let y = win[1]; y < win[3]; y++) {
            for (let x = win[0]; x < win[2]; x++) {
              if (sampleLogit(emb.scale, m.grid, x + 0.5, y + 0.5) > 0) {
                pbin[y * aw + x] = 255;
              }
            }
          }
        }
        // 扣除岩点并集（外扩 1px 防 halo）：岩点归线路，人物只留皮肤/衣物
        const hbin = new Uint8Array(n);
        for (const hd of holds) {
          for (const i of hd.ownPixels) hbin[i] = 255;
        }
        const hdil = morphDilate(hbin, aw, ah, 1);
        const px: number[] = [];
        for (let i = 0; i < n; i++) {
          if (pbin[i] > 0 && hdil[i] === 0) px.push(i);
        }
        if (px.length >= 24) person = Uint32Array.from(px);
      }
      } catch (pe) {
        console.warn("[grip] 人物检测失败（可选能力，跳过人物保色）：", pe);
      }
    }
  }

  if (holds.length === 0) {
    // 回退：整图墙面差分（对拍 lib.rs:802-930）
    cb("整图墙面差分（回退）");
    const lab = labImage(img, n);
    const field = buildWallField(lab, sat, aw, ah);
    let mask: Uint8Array = new Uint8Array(n);
    for (let y = 0; y < ah; y++) {
      for (let x = 0; x < aw; x++) {
        const i = y * aw + x;
        const { lab: r, sigma } = wallRef(field, x, y);
        const dd = Math.sqrt(
          (lab[i * 3] - r[0]) ** 2 +
            (lab[i * 3 + 1] - r[1]) ** 2 +
            (lab[i * 3 + 2] - r[2]) ** 2,
        );
        const t = Math.min(Math.max(p.wallK * sigma, 6.0), 30.0);
        if (dd > t) mask[i] = 255;
      }
    }
    mask = morphOpen(mask, aw, ah, 2);
    mask = morphDilate(mask, aw, ah, 2);
    const lbl = labelComponents(mask, aw, ah);
    const stats = gatherStats(lbl.labels, hue, sat, val, aw, ah, lbl.count);
    labels = lbl.labels;
    const oversized: Array<(typeof stats)[number]> = [];
    for (const s of stats) {
      if (s.area === 0 || s.area < p.minAreaFrac * n) continue;
      const kind = classifyStats(s, p.minColoredFrac);
      const maxFracFor =
        kind === HoldKind.Colored ? p.maxColoredAreaFrac : p.maxAreaFrac;
      const af = s.area / n;
      if (af > maxFracFor) {
        if (af <= 0.3 && s.bw() * s.bh() <= 0.36 * n) oversized.push(s);
        continue;
      }
      if (s.fill() < p.minFillRatio) continue;
      if (kind === HoldKind.Black) {
        if (s.area < p.minAreaFrac * 1.6 * n) continue;
        if (s.y1 > 0.95 * ah) continue;
      } else if (kind === HoldKind.Gray) {
        if (
          s.satMean() > 0.13 ||
          s.fill() < Math.min(p.minFillRatio + 0.12, 0.6) ||
          s.aspect() > 3.2
        )
          continue;
        if (s.area < p.minAreaFrac * 2.0 * n) continue;
        if (s.y1 > 0.93 * ah) continue;
      }
      const [modeHue, purity] = s.modeHue();
      const ab: [number, number] = [0, 0];
      if (kind === HoldKind.Colored) {
        let as_ = 0,
          bs = 0,
          cw2 = 0;
        for (let y = s.y0; y < s.y1; y++) {
          for (let x = s.x0; x < s.x1; x++) {
            const i = y * aw + x;
            if (
              labels[i] === s.label &&
              sat[i] >= 0.22 &&
              val[i] >= 0.1 &&
              hueDist(hue[i], modeHue) <= 15
            ) {
              as_ += lab[i * 3 + 1];
              bs += lab[i * 3 + 2];
              cw2++;
            }
          }
        }
        if (cw2 >= 6) {
          ab[0] = as_ / cw2;
          ab[1] = bs / cw2;
        }
      }
      holds.push({
        id: holds.length,
        label: s.label,
        ownPixels: new Uint32Array(0),
        area: s.area,
        bbox: s.bboxArr(),
        centroid: s.centroid(),
        hue: kind === HoldKind.Colored ? modeHue : 0,
        ab,
        sat: s.satMean(),
        val: s.valMean(),
        purity,
        kind,
        cluster: -1,
        lasso: false,
      });
    }
    // 超大块内部补测
    oversized.sort((a2, b2) => b2.area - a2.area);
    oversized.length = Math.min(oversized.length, 8);
    for (const s of oversized) {
      const found = wallDistHoldsInCrop(
        img,
        aw,
        s.x0,
        s.y0,
        s.bw(),
        s.bh(),
        undefined,
        p,
        Math.trunc(minPx * 0.5),
        Math.trunc(p.maxColoredAreaFrac * 1.2 * n),
      );
      for (const f of found) {
        if (isDup(holds, f, 0.7)) continue;
        f.id = holds.length;
        holds.push(f);
      }
    }
  }

  cb("颜色聚类分线");
  const clusters = buildClusters(holds, p.colorTolAb);

  // 墙面统计（展示用）
  const vs: number[] = [];
  const ss: number[] = [];
  for (let i = 0; i < n; i += 7) {
    if (sat[i] < 0.2) {
      vs.push(val[i]);
      ss.push(sat[i]);
    }
  }
  let wallV = 0.5;
  let wallS = 0.15;
  if (vs.length > 0) {
    vs.sort((a2, b2) => a2 - b2);
    ss.sort((a2, b2) => a2 - b2);
    wallV = vs[vs.length >> 1];
    wallS = ss[ss.length >> 1];
  }

  return {
    aw,
    ah,
    fw,
    fh,
    labels,
    holds,
    clusters,
    img,
    hue,
    sat,
    val,
    person,
    wallV,
    wallS,
    timingMs: performance.now() - t0,
  };
}
